
import { useEffect, useRef } from 'react';
import useWebSocketLib from 'react-use-websocket'; //Lib pour gérer WebSocket facilement
import { useSelector } from 'react-redux'; //Accès au token d'auth

// ─────────────────────────────────────────────
//  CONFIGURATION URL WEBSOCKET
// ─────────────────────────────────────────────
const WS_BASE_URL = process.env.REACT_APP_WS_URL || 'wss://api.dev-test.secufire.cloud/api/auth';

// ─────────────────────────────────────────────
//  HOOK PRINCIPAL
// handlers = {
//   "event.name": (data) => {}
// }
// ─────────────────────────────────────────────
const useAppWebSocket = (handlers = {}) => {

  // Récupération du token et état d'authentification
  const { accessToken, isAuthenticated } = useSelector((s) => s.auth);

  // Référence pour stocker l’interval du ping
  const pingIntervalRef = useRef(null);

  // ─────────────────────────────────────────────
  //  OBJECTIF : éviter de recréer la connexion WS
  // à chaque re-render si les handlers changent
  // ─────────────────────────────────────────────
  const handlersRef = useRef(handlers);

  useEffect(() => {
    handlersRef.current = handlers;
  });

  // ─────────────────────────────────────────────
  //  CONSTRUCTION DE L’URL WEBSOCKET
  // - Si pas authentifié → PAS de connexion
  // - Sinon → connexion avec token
  // ─────────────────────────────────────────────
  const socketUrl =
    isAuthenticated && accessToken
      ? `${WS_BASE_URL}/ws/connect?token=${accessToken}`
      : null;

  // ─────────────────────────────────────────────
  //  INITIALISATION WEBSOCKET
  // ─────────────────────────────────────────────
  const { sendMessage, lastMessage, readyState } =
    useWebSocketLib(socketUrl, {

      // ─────────────────────────────────────────
      //  RECONNEXION AUTOMATIQUE
      // Backoff exponentiel :
      // 1s → 2s → 4s → ... → max 30s
      // ─────────────────────────────────────────
      shouldReconnect: () => true,
      reconnectAttempts: 10,
      reconnectInterval: (attempt) =>
        Math.min(1000 * 2 ** attempt, 30_000),

      // ─────────────────────────────────────────
      //  À L’OUVERTURE DE LA CONNEXION
      // ─────────────────────────────────────────
      onOpen: () => {
        console.debug('[WS] Connected');

        //  Ping toutes les 30s pour :
        // - garder la connexion active
        // - signaler au backend que l'utilisateur est online
        pingIntervalRef.current = setInterval(() => {
          sendMessage(JSON.stringify({ type: 'ping' }));
        }, 30_000);
      },

      // ─────────────────────────────────────────
      //  À LA FERMETURE DE LA CONNEXION
      // ─────────────────────────────────────────
      onClose: () => {
        console.debug('[WS] Disconnected');

        // 🧹 Nettoyage du ping
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
      },

      // ─────────────────────────────────────────
      //  GESTION DES ERREURS
      // ─────────────────────────────────────────
      onError: (e) => {
        console.error('[WS] Error', e);
      },
    });

  // ─────────────────────────────────────────────
  //  CLEANUP GLOBAL (UNMOUNT)
  // ─────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    };
  }, []);

  // ─────────────────────────────────────────────
  //  TRAITEMENT DES MESSAGES ENTRANTS
  // ─────────────────────────────────────────────
  useEffect(() => {

    if (!lastMessage?.data) return;

    let parsed;

    // Étape 1 : PARSE (string → objet JS)
    try {
      parsed = JSON.parse(lastMessage.data);
    } catch {
      //  Si message non JSON → ignoré
      return;
    }

    const { event, data } = parsed;


    if (!event) return;

    // ─────────────────────────────────────────
    //  ÉVÉNEMENTS INTERNES (ignorés)
    // ─────────────────────────────────────────
    if (event === 'connected') {
      console.debug('[WS] Ack:', data);
      return;
    }

    if (event === 'pong') return;

    // ─────────────────────────────────────────
    //  DISPATCH DYNAMIQUE
    // On appelle le bon handler selon l’événement
    // ─────────────────────────────────────────
    const handler = handlersRef.current[event];

    if (typeof handler === 'function') {
      handler(data);
    } else {
      console.debug('[WS] No handler for event:', event, data);
    }

  }, [lastMessage]);

  // ─────────────────────────────────────────────
  //  ÉTAT DE CONNEXION
  // readyState === 1 → CONNECTED
  // ─────────────────────────────────────────────
  const isConnected = readyState === 1;

  // ─────────────────────────────────────────────
  // 📤 RETOUR DU HOOK
  // ─────────────────────────────────────────────
  return {
    isConnected,   // bool → connecté ou non
    readyState,    // état brut WS
    lastMessage,   // dernier message reçu
    sendMessage,   // fonction pour envoyer message
  };
};

export default useAppWebSocket;